import { cn } from "@perceptron/utils/cn";

import { getLinkHost } from "@demo/libs/tool-calls/favicon";
import type { ToolCallLinkEntry } from "@demo/server/api/contracts/toolCall";
import { strings } from "@demo/utils/strings";

import Favicon from "./Favicon";
import { rowEntrance } from "./rowEntrance";
import { ShimmerText } from "./ShimmerText";

export interface VisitResultsListProps {
  /** The information goal the pages are read toward. */
  goal?: string;
  /** The pages being visited, in order; `index` on a result matches its position here. */
  urls: string[];
  /** One per page reported so far, in order. */
  results: ToolCallLinkEntry[];
  isStreaming: boolean;
}

const hostOf = (url: string): string | undefined => getLinkHost({ url });

/**
 * The page a running visit is on: pages report in order, so it is the first one without a
 * result. Once every page has reported (or the call ended) it is the last page.
 */
export function describeVisitProgressHost(urls: string[], reportedCount: number): string | undefined {
  const current = urls[Math.min(reportedCount, Math.max(urls.length - 1, 0))];
  return current ? hostOf(current) : undefined;
}

const SectionLabel = ({ children }: { children: string }) => (
  <span className="whitespace-nowrap text-foreground-disabled label-2">{children}</span>
);

/**
 * The expanded body of `search.visit`: an Input section with the goal and one favicon row per
 * page (rows shimmer until their page has reported), and a Result section with each reported
 * page's summary as plain text. Summaries never go through the markdown pipeline.
 */
function VisitResultsList({ goal, urls, results, isStreaming }: VisitResultsListProps) {
  const reported = new Map(results.map(({ index, link }) => [index, link]));
  const copy = strings.toolCall.visit;

  return (
    <div className="flex w-full flex-col gap-5 px-2 pb-2 pt-4">
      <div className="flex w-full flex-col gap-2">
        <SectionLabel>{copy.inputLabel}</SectionLabel>
        {goal && (
          <p className="w-full text-foreground-primary label-2" data-testid="visit-goal">
            “{goal}”
          </p>
        )}
        <ul className="flex w-full flex-col gap-2">
          {urls.map((url, index) => {
            const host = hostOf(url);
            const label = host ?? url;
            // A page still shimmers until its result has arrived; once reported it settles.
            const pending = isStreaming && !reported.has(index);
            const entrance = rowEntrance(index, isStreaming);
            return (
              <li
                className={cn("flex w-full items-center gap-2", entrance.className)}
                data-index={index}
                data-testid="visit-page-row"
                key={index}
                style={entrance.style}
              >
                <Favicon alt={label} host={host} key={host} url={url} />
                <a className="group min-w-px truncate" href={url} rel="noreferrer noopener" target="_blank" title={url}>
                  <ShimmerText
                    className="truncate label-2"
                    data-testid="visit-page-label"
                    highlightOnHover
                    isStreaming={pending}
                    settledTone="secondary"
                  >
                    {label}
                  </ShimmerText>
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      {results.length > 0 && (
        <div className="flex w-full flex-col gap-2" data-testid="visit-results">
          <SectionLabel>{copy.resultLabel}</SectionLabel>
          {results.map(({ index, link }) => {
            const status = link.status ?? "ok";
            const host = getLinkHost(link) ?? link.url;
            const entrance = rowEntrance(index, isStreaming);
            return (
              <div
                className={cn("flex w-full flex-col gap-1", entrance.className)}
                data-index={index}
                data-testid="visit-result"
                key={index}
                style={entrance.style}
              >
                {urls.length > 1 && <span className="text-foreground-secondary label-2">{host}</span>}
                {status === "ok" && link.summary ? (
                  // Plain escaped text with line breaks preserved: page content never reaches the
                  // markdown or annotation pipeline, so a page cannot inject boxes into the run.
                  <p className="whitespace-pre-wrap text-foreground-primary label-2">{link.summary}</p>
                ) : (
                  <p className="text-foreground-secondary label-2">
                    {strings.toolCall.linkStatus[status === "ok" ? "not_processed" : status]}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default VisitResultsList;
